(() => {
  const ELEMENT_NAME = 'featured-collection-product-rail';
  const DESKTOP_QUERY = '(min-width: 990px)';

  if (
    typeof customElements === 'undefined'
    || typeof HTMLElement === 'undefined'
    || customElements.get(ELEMENT_NAME)
  ) return;

  class FeaturedCollectionProductRail extends HTMLElement {
    constructor() {
      super();
      this.connected = false;
      this.embla = null;
      this.emblaControlCleanup = null;
      this.emblaProximity = null;
      this.preparedSlides = [];
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.mountCarousel = this.mountCarousel.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;

      this.viewport = this.querySelector('[data-featured-collection-product-rail]');
      this.container = this.querySelector('[data-featured-collection-product-container]');
      this.controls = this.querySelector('[data-featured-collection-rail-controls]');
      this.previousButton = this.querySelector('[data-featured-collection-rail-previous]');
      this.nextButton = this.querySelector('[data-featured-collection-rail-next]');
      this.productsPerRow = Math.max(1, parseInt(this.getAttribute('data-products-per-row'), 10) || 4);
      this.productGap = Math.max(0, parseFloat(this.getAttribute('data-product-gap')) || 0);

      if (!this.viewport || !this.container || !this.controls || !this.previousButton || !this.nextButton) return;

      this.desktopQuery.addEventListener?.('change', this.handleBreakpointChange);
      this.connected = true;
      this.hideControls();
      this.syncBreakpoint();
    }

    disconnectedCallback() {
      this.desktopQuery.removeEventListener?.('change', this.handleBreakpointChange);
      this.deactivateDesktopRail();
      this.connected = false;
    }

    handleBreakpointChange() {
      this.syncBreakpoint();
    }

    syncBreakpoint() {
      if (this.desktopQuery.matches) this.activateDesktopRail();
      else this.deactivateDesktopRail();
    }

    activateDesktopRail() {
      if (this.embla || this.emblaProximity || !this.viewport) return;

      const manager = window.NordwegEmbla;
      if (!manager) {
        this.hideControls();
        return;
      }

      this.preparedSlides = Array.from(this.container.children);
      if (this.preparedSlides.length === 0) {
        this.hideControls();
        return;
      }

      this.viewport.classList.add('embla');
      this.container.classList.add('embla__container');
      this.preparedSlides.forEach((slide) => slide.classList.add('embla__slide'));
      manager.setSlideMetrics(this.preparedSlides, this.productsPerRow, this.productGap);
      this.emblaProximity = manager.observeProximity(this, this.mountCarousel);
    }

    mountCarousel() {
      if (this.embla) return this.embla;

      const manager = window.NordwegEmbla;
      this.embla = manager?.create(this.viewport, {
        loop: false,
        slidesToScroll: 1,
        watchDrag: (_api, event) => !event.target?.closest?.(
          '.collection-card-gallery, .collection-card-gallery *',
        ),
      });
      if (!this.embla) {
        this.hideControls();
        return false;
      }

      this.emblaControlCleanup = manager.bindControls(
        this.embla,
        [this.previousButton],
        [this.nextButton],
        { rewind: false },
      );
      this.controls.hidden = this.embla.scrollSnapList().length <= 1;
      this.toggleAttribute('data-featured-collection-rail-ready', !this.controls.hidden);
      return this.embla;
    }

    deactivateDesktopRail() {
      this.emblaProximity?.disconnect();
      this.emblaProximity = null;
      this.emblaControlCleanup?.();
      this.emblaControlCleanup = null;
      if (this.embla) window.NordwegEmbla?.destroy(this.embla, this.viewport);
      this.embla = null;
      if (this.preparedSlides.length > 0) this.restoreStaticViewport();
      this.preparedSlides = [];
      this.removeAttribute('data-featured-collection-rail-ready');
      this.hideControls();
    }

    restoreStaticViewport() {
      if (!this.viewport) return;

      for (const slide of this.preparedSlides) {
        slide.classList.remove('embla__slide');
        slide.style.removeProperty('flex');
        slide.style.removeProperty('min-width');
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      }

      this.container?.classList.remove('embla__container');
      this.container?.style.removeProperty('gap');
      this.viewport.classList.remove('embla');
      this.viewport.removeAttribute('data-embla-prepared');
      this.viewport.removeAttribute('data-embla-mounted');
      this.viewport.removeAttribute('data-embla-failed');
    }

    hideControls() {
      if (this.controls) this.controls.hidden = true;
      for (const button of [this.previousButton, this.nextButton]) {
        if (!button) continue;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    }
  }

  customElements.define(ELEMENT_NAME, FeaturedCollectionProductRail);
})();
